/**
 * Click-to-fix banner for spec-validator issues.
 *
 * Renders in edit mode at the top of the dashboard when validateSpec
 * found issues with auto-fixable codes. Each issue becomes a chip
 * showing the code + element path; clicking "Fix" applies the fixer
 * and emits onSpecChange so the renderer's edit-mode parent can
 * re-validate on the next render.
 *
 * Issues whose code isn't in {@link AUTO_FIXABLE_CODES} render as
 * info chips without a Fix button — the author can see them but the
 * remediation is structural and needs human judgement.
 */

import { useMemo } from "react"

import { AlertTriangle, Wand2 } from "@/components/icons"

import type { DashboardSpec } from "../dashboard/catalog"
import type { SpecIssue, SpecValidationResult } from "../utils/spec-validator"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

import {
  applyAllAutoFixableSpecIssues,
  applySpecFix,
  AUTO_FIXABLE_CODES,
} from "../utils/spec-fixers"
import { validateSpec } from "../utils/spec-validator"

export interface SpecIssuesBannerProps {
  spec: DashboardSpec
  /** Pre-computed validation result; recomputed from `spec` if absent. */
  result?: SpecValidationResult
  onSpecChange?: (spec: DashboardSpec) => void
}

export function SpecIssuesBanner({ spec, result, onSpecChange }: SpecIssuesBannerProps) {
  const validation = useMemo(() => result ?? validateSpec(spec), [result, spec])
  if (!validation.issues.length) return null

  const fixable = validation.issues.filter((i) => AUTO_FIXABLE_CODES.has(i.code))
  const informational = validation.issues.filter((i) => !AUTO_FIXABLE_CODES.has(i.code))

  function handleFix(issue: SpecIssue) {
    if (!onSpecChange) return
    const { spec: nextSpec, changed } = applySpecFix(spec, issue)
    if (changed) onSpecChange(nextSpec)
  }

  function handleFixAll() {
    if (!onSpecChange) return
    const { spec: nextSpec, changed } = applyAllAutoFixableSpecIssues(spec)
    if (changed) onSpecChange(nextSpec)
  }

  return (
    <div className="border-warning/30 bg-warning/10 dark:border-warning/40 dark:bg-warning/15 mb-3 flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="text-warning size-4" />
        <span className="text-foreground text-sm font-medium">
          {validation.issues.length === 1
            ? "1 spec issue"
            : `${validation.issues.length} spec issues`}
        </span>
        {fixable.length > 1 && onSpecChange && (
          <Button
            className="ml-auto h-6 px-2 text-xs"
            size="sm"
            variant="outline"
            onClick={handleFixAll}
          >
            <Wand2 className="mr-1 size-3" />
            Fix all ({fixable.length})
          </Button>
        )}
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {fixable.map((issue, i) => (
          <li
            key={`${issue.code}-${issue.elementPath ?? i}`}
            className="bg-background/70 border-warning/30 inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs"
          >
            <Badge
              className="font-mono text-[10px]"
              variant={issue.severity === "error" ? "destructive" : "outline"}
            >
              {issue.code}
            </Badge>
            {issue.elementPath && (
              <code className="text-muted-foreground font-mono text-[11px]">
                {issue.elementPath}
              </code>
            )}
            {onSpecChange && (
              <Button
                aria-label={`Fix ${issue.code}`}
                className="-my-0.5 ml-1 h-6 px-1.5 text-[11px]"
                size="sm"
                variant="ghost"
                onClick={() => handleFix(issue)}
              >
                Fix
              </Button>
            )}
          </li>
        ))}
        {informational.map((issue, i) => (
          <li
            key={`info-${issue.code}-${i}`}
            className="bg-background/70 text-muted-foreground border-warning/20 inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs"
            title={issue.message}
          >
            <Badge className="font-mono text-[10px]" variant="secondary">
              {issue.code}
            </Badge>
            {issue.elementPath && (
              <code className="text-muted-foreground font-mono text-[11px]">
                {issue.elementPath}
              </code>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
